import { useEffect, useState } from "react";
import entryTestClient from "../../client/EntryTestClient";
import { EntranceTestBookingInfo, EntranceTestBookingInfoValidator } from "../../models/EntranceTestBookingInfo";
import { validateBookingDate } from "../../utils/validatorUtils";
import { SelectCheckbox } from "../FormComponents/CheckBox";
import { dayToNumber, time12HourTo24Hour, time24HourTo12Hour } from "../FormComponents/EntranceTestTimeSlotsUtils";
import { InputBookingDate } from "../FormComponents/InputTextBox";
import { Select } from "../FormComponents/Select";

export const EntranceTestBookingSection = (props: {
    bookingInfo: EntranceTestBookingInfo;
    consume: (val: EntranceTestBookingInfo) => void;
    checkForCompletion: (val: boolean) => void;
    bookingInfoValidator: EntranceTestBookingInfoValidator;
}) => {
    const [bookingInfo, setBookingInfo] = useState<EntranceTestBookingInfo>(props.bookingInfo);
    const [validBookingDate, setValidBookingDate] = useState(validateBookingDate(props.bookingInfo.date));
    const [timeSlots, setTimeSlots] = useState<any>([]);
    const [unavailableTimes, setUnavailableTimes] = useState<any>([]);

    const [selectKey, setSelectKey] = useState(0);

    const bookingStageComplete = () => {
        if (
            bookingInfo.location === null ||
            bookingInfo.location === "" ||
            bookingInfo.date === null ||
            bookingInfo.date === "" ||
            bookingInfo.testTime === null ||
            bookingInfo.testTime === "" ||
            bookingInfo.testTime === "Select Option" ||
            !validateBookingDate(bookingInfo.date)
        ) {
            return false;
        }
        return true;
    };

    useEffect(() => {
        props.consume(bookingInfo);
        const formComplete = bookingStageComplete();
        props.checkForCompletion(formComplete);
    }, [bookingInfo]);

    const fetchTimeslots = async (date: string, location: string) => {
        if (date && location) {
            // Fetch timeslots for this date/location
            const timeslotDtos = await entryTestClient.getTimeslots(date, location);
            // Convert DTOs from API to a more usable format:
            // {
            //     "Burwood": {
            //         0: [
            //             "1:00PM",
            //             "3:30PM",
            //             "5:00PM",
            //         ],
            //         1: [
            //             "10:30AM",
            //             "12:00PM",
            //             "3:30PM",
            //             "5:00PM"
            //         ],
            //         2: [
            // etc.
            const timeslotMappings = timeslotDtos.reduce((acc: any, timeslotDto: any) => {
                const { centre, day, startTime } = timeslotDto;
                const centreName = centre.name;
                const dayNumber = dayToNumber[day];
                if (!acc[centreName]) {
                    acc[centreName] = {};
                }
                if (!acc[centreName][dayNumber]) {
                    acc[centreName][dayNumber] = [];
                }
                acc[centreName][dayNumber].push(time24HourTo12Hour(startTime));
                return acc;
            }, {});
            setBookingInfo((prevState) => ({ ...prevState, testTime: "Select Option" }));

            if (Object.keys(timeslotMappings).length > 0) {
                // Fetch capacity for each timeslot
                const dayOfWeek = new Date(date).getDay();
                let availableTimeSlots = timeslotMappings[location][dayOfWeek];
                const currentDate = new Date();
                currentDate.setHours(0, 0, 0, 0);
                let dateAtMidnight = new Date(date);
                dateAtMidnight.setHours(0, 0, 0, 0);
                if (dateAtMidnight.getTime() === currentDate.getTime()) {
                    const currentTime = new Date();
                    availableTimeSlots = availableTimeSlots.filter((slot: any) => {
                        const slotTime = new Date(date);
                        const [hours, minutes, _] = time12HourTo24Hour(slot).split(":");
                        slotTime.setHours(parseInt(hours), parseInt(minutes));
                        return slotTime > currentTime;
                    });
                }
                try {
                    const slots = await Promise.all(
                        availableTimeSlots.map(async (t: any) => {
                            const time_capacity = await entryTestClient.getCapacityByDateTimeCentre(
                                t,
                                date.toString(),
                                location,
                            );
                            return { time: t, capacity: time_capacity };
                        }),
                    );
                    setTimeSlots(slots);
                    setUnavailableTimes(slots?.filter((t) => t.capacity < 1));
                } catch (error) {
                    console.error("Error: ", error);
                }
            } else {
                setTimeSlots([]);
                setUnavailableTimes([]);
            }
        } else {
            setTimeSlots([]);
            setUnavailableTimes([]);
            setBookingInfo((prevState) => ({ ...prevState, testTime: "Select Option" }));
        }

        setSelectKey((prevKey) => prevKey + 1);
    };

    return (
        <>
            Please select where you would like to take the test
            <SelectCheckbox
                optionOne="Burwood"
                optionTwo="Chatswood"
                optionThree="Hurstville"
                value={bookingInfo.location}
                consume={async (n) => {
                    setBookingInfo((prevState) => ({ ...prevState, location: n }));
                    await fetchTimeslots(bookingInfo.date, n);
                }}
                errorMessage={props.bookingInfoValidator.location}
            />
            Please select the date you would like to sit the test:
            <InputBookingDate
                placeholder="dd/mm/yyyy"
                value={bookingInfo.date}
                consume={async (n: any) => {
                    setBookingInfo((prevState) => ({ ...prevState, date: n }));
                    await fetchTimeslots(n, bookingInfo.location);
                }}
                checkIfValid={(b: boolean) => setValidBookingDate(b)}
                errorMessage={props.bookingInfoValidator.date}
            />
            {validBookingDate ? (
                <>
                    <Select
                        key={`test-time-select-${selectKey}`}
                        consume={(n: any) => {
                            setBookingInfo((prevState) => ({ ...prevState, testTime: n.split(" - ")[0] }));
                        }}
                        placeholder="Select Option"
                        initialValue={bookingInfo.testTime}
                        values={
                            timeSlots?.map(
                                (slot: any) =>
                                    `${slot.time} - ${slot.capacity <= 0 ? `FULL` : `${slot.capacity} available spots`}`,
                            ) || []
                        }
                        disabledValues={unavailableTimes?.map((slot: any) => `${slot.time} - FULL`) || []}
                        errorMessage={props.bookingInfoValidator.testTime}
                    />
                </>
            ) : (
                <></>
            )}
        </>
    );
};
